import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Check,
  KeyRound,
  LockKeyhole,
  ShieldCheck,
  ShieldX,
  UsersRound,
  X,
} from "lucide-react";
import { listPermissionMatrix, updateRolePermission } from "../api";
import type { PermissionMatrixRow } from "../types";

const roles = [
  "superadmin",
  "fleet_admin",
  "operator",
  "dispatcher",
  "maintenance",
  "auditor",
  "client",
];

const roleLabel: Record<string, string> = {
  superadmin: "SUPERADMIN",
  fleet_admin: "ADMIN FLOTA",
  operator: "OPERADOR",
  dispatcher: "DESPACHO",
  maintenance: "TALLER",
  auditor: "AUDITOR",
  client: "CLIENTE",
};

export function PermissionMatrixPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<PermissionMatrixRow[]>([]);
  const [pendingKey, setPendingKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listPermissionMatrix()
        .then((items) => {
          setRows(items);
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar permisos"),
        );
    };
    refresh();
    window.addEventListener("gsf:permissions-updated", refresh);
    return () => window.removeEventListener("gsf:permissions-updated", refresh);
  }, [open]);

  const summary = useMemo(
    () => ({
      permissions: rows.length,
      grants: rows.reduce(
        (total, row) => total + Object.values(row.roles).filter(Boolean).length,
        0,
      ),
      categories: new Set(rows.map((row) => row.category)).size,
    }),
    [rows],
  );

  async function toggle(row: PermissionMatrixRow, role: string) {
    const key = `${row.code}:${role}`;
    setPendingKey(key);
    setError("");
    try {
      const updated = await updateRolePermission(role, row.code, !row.roles[role]);
      setRows((current) =>
        current.map((item) => item.code === updated.code ? updated : item),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar el permiso");
    } finally {
      setPendingKey("");
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel permission-panel" role="dialog" aria-modal="true" aria-labelledby="permission-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">CONTROL DE ACCESO · MATRIZ RBAC PERSISTENTE</span>
            <h2 id="permission-title">Permisos por rol</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar permisos"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><KeyRound size={17} /><b>{summary.permissions}</b> capacidades</span>
          <span><ShieldCheck size={17} /><b>{summary.grants}</b> concesiones</span>
          <span><UsersRound size={17} /><b>{roles.length}</b> roles</span>
        </section>
        <div className="permission-panel__body">
          <div className="permission-warning">
            <LockKeyhole size={18} />
            <span><strong>CAMBIOS EN VIVO Y AUDITADOS</strong><small>La próxima solicitud del usuario utilizará inmediatamente esta matriz.</small></span>
          </div>
          <div className="permission-table-wrap">
            <table className="permission-table">
              <thead>
                <tr>
                  <th>CAPACIDAD</th>
                  {roles.map((role) => <th key={role}>{roleLabel[role]}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <Fragment key={row.code}>
                    {(index === 0 || rows[index - 1].category !== row.category) && (
                      <tr className="permission-category">
                        <td colSpan={roles.length + 1}>{row.category.toUpperCase()}</td>
                      </tr>
                    )}
                    <tr>
                      <td><strong>{row.name}</strong><small>{row.code}</small><em>{row.description}</em></td>
                      {roles.map((role) => {
                        const allowed = Boolean(row.roles[role]);
                        const locked =
                          role === "superadmin" && row.code === "permissions.manage";
                        return (
                          <td key={role}>
                            <button
                              type="button"
                              className={allowed ? "is-allowed" : ""}
                              disabled={locked || pendingKey === `${row.code}:${role}`}
                              onClick={() => toggle(row, role)}
                              aria-label={`${allowed ? "Retirar" : "Conceder"} ${row.name} a ${roleLabel[role]}`}
                              title={locked ? "Protección contra bloqueo del superadmin" : undefined}
                            >
                              {allowed ? <Check size={13} /> : <ShieldX size={13} />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
